import React, { useEffect, useRef, useState, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { PLATFORM_CONFIGS, PlatformKey } from '@/utils/platformConfig';

type Props = {
  platform: PlatformKey;
  input: string;
  className?: string;
  onError?: () => void;
  onLoad?: () => void;
  deferLoad?: boolean;
  aspect?: number; // kept for external contexts, unused in carousel
};

/** Sanitize embed HTML to prevent XSS attacks */
const sanitizeEmbedHTML = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['iframe', 'blockquote', 'script', 'a', 'p', 'br', 'div', 'span'],
    ALLOWED_ATTR: ['src', 'width', 'height', 'frameborder', 'allowfullscreen', 'scrolling', 'class', 'data-*', 'href', 'target', 'rel'],
    ALLOW_DATA_ATTR: true,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    SANITIZE_DOM: true,
    KEEP_CONTENT: true
  });
};

/** Minimal, inlined script loader that only injects once per src */
const ensureScript = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Already present?
    const existing = Array.from(document.scripts).find(s => s.src === src);
    if (existing) {
      // If the global SDK is already ready, resolve immediately; otherwise wait a tick
      return resolve();
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    // Some SDKs rely on charset when loaded cross-origin (Twitter is fine without, but harmless)
    script.charset = 'utf-8';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
};

export const ResponsiveEmbed: React.FC<Props> = React.memo(
  ({ platform, input, className, onError, onLoad, deferLoad = false, aspect = 1.777 }) => {
    // aspect parameter kept for external contexts, unused in carousel
    void aspect;
    const cfg = PLATFORM_CONFIGS[platform];
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [shouldLoad, setShouldLoad] = useState(!deferLoad);
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const innerRef = useRef<HTMLDivElement | null>(null);

    const onErrorRef = useRef(onError);
    const onLoadRef = useRef(onLoad);
    useEffect(() => { onErrorRef.current = onError; }, [onError]);
    useEffect(() => { onLoadRef.current = onLoad; }, [onLoad]);

    // If the input is an iframe HTML string, extract src; otherwise pass through as-is
    const extractSrcFromIframe = (s: string) =>
      s.match(/<iframe[^>]*src=["']([^"']+)["']/i)?.[1] ?? s;
    const inputForDetect = typeof input === 'string' ? extractSrcFromIframe(input) : input;

    const renderObj = useMemo(() => {
      try {
        return cfg.build(inputForDetect);
      } catch {
        return null;
      }
    }, [cfg, inputForDetect]);

    useEffect(() => {
      let cancelled = false;

      async function hydrate() {
        if (!renderObj) return;
        setIsLoading(true);
        setError(null);

        try {
          // Load any required SDKs (Twitter/Instagram, etc.)
          if (renderObj.scripts?.length) {
            for (const src of renderObj.scripts) {
              await ensureScript(src);
            }
          }

          // Give SDKs a beat to register DOM
          setTimeout(() => {
            if (cancelled) return;

            try {
              switch (platform) {
                case 'twitter':
                  // Both initial parse and rerender safety
                  window.twttr?.widgets?.load?.(innerRef.current);
                  break;
                case 'instagram':
                  window.instgrm?.Embeds?.process?.();
                  break;
                case 'facebook':
                  window.FB?.XFBML?.parse?.(innerRef.current || undefined);
                  break;
              }
            } catch {
              /* ignore; process() below may still handle it */
            }

            // Optional per-platform post-process hook from config
            try {
              renderObj.process?.();
            } catch {
              /* ignore */
            }

            if (!cancelled) {
              setIsLoading(false);
              onLoadRef.current?.();
            }
          }, 100);
        } catch (e) {
          if (!cancelled) {
            setError('Failed to load embed');
            setIsLoading(false);
            onErrorRef.current?.();
          }
        }
      }

      // Only hydrate when we should actually load
      if (shouldLoad) hydrate();
      return () => { cancelled = true; };
    }, [renderObj, platform, shouldLoad]);

    // Lazy/defer loading – start when visible
    useEffect(() => {
      if (!deferLoad || shouldLoad) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        },
        { rootMargin: '300px' }
      );
      if (wrapRef.current) observer.observe(wrapRef.current);
      return () => observer.disconnect();
    }, [deferLoad, shouldLoad]);

    if (!renderObj) return null;

    // Only Instagram/Twitter use scrollable blockquote behavior
    const isScrollable = platform === 'instagram' || platform === 'twitter';
    const scrollClass =
      platform === 'instagram'
        ? 'instagram-scrollable'
        : platform === 'twitter'
        ? 'twitter-scrollable'
        : '';

    return (
      <div className={`relative w-full h-full ${className || ''}`} ref={wrapRef}>
        {!shouldLoad ? (
          <div className="flex items-center justify-center h-full theme-muted rounded-lg">
            <button
              onClick={() => setShouldLoad(true)}
              className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg border border-[color-mix(in_srgb,var(--accent-primary) 65%, transparent)] hover:bg-[color-mix(in_srgb,var(--accent-primary) 85%, #ffffff 15%)] transition-colors"
            >
              Load {platform}
            </button>
          </div>
        ) : (
          <>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center theme-muted bg-opacity-75 z-10">
                <div className="text-sm theme-text-muted">Loading embed...</div>
              </div>
            )}

            <div
              ref={innerRef}
              className={`${
                isScrollable ? 'carousel-embed-content-scrollable' : 'carousel-embed-content'
              } asset-embed ${scrollClass}`}
              onWheelCapture={(e) => e.stopPropagation()}
              onTouchMoveCapture={(e) => e.stopPropagation()}
              dangerouslySetInnerHTML={{ __html: sanitizeEmbedHTML(renderObj.html) }}
            />

            {!!error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded">
                <p className="text-sm text-red-600">Could not load embed. {error}</p>
                <a className="text-sm underline" href={input} target="_blank" rel="noopener noreferrer">
                  Open original
                </a>
              </div>
            )}
          </>
        )}
      </div>
    );
  },
  (prev, next) =>
    prev.platform === next.platform &&
    prev.input === next.input &&
    prev.className === next.className
);